import moment from 'moment';
import {
  parseNotificationBody,
  normalizeExternalLink,
  sanitizeHtmlContent,
  stripHtmlTags,
} from '../../../views/alerts/utils';
import {
  CLAIM_EMAIL_APPROVAL_NOTIFICATION_TYPE,
  CLAIM_CREATED_NOTIFICATION_TYPE,
  STALLED_CLAIM_NOTIFICATION_TYPE,
} from '../../../views/alerts/actions';

export const getUnreadTrackingCount = (notifications: any[]): number => {
  return (notifications || []).reduce((count: number, notification: any) => {
    const trackingRecords = Array.isArray(notification?.trackingRecords)
      ? notification.trackingRecords
      : [];
    return count + trackingRecords.filter((record: any) => record && record.read === false).length;
  }, 0);
};

export const getNotificationType = (notification: any): string => {
  const typeValue = notification?.type || notification?.notificationType || notification?.category;
  return typeof typeValue === 'string' ? typeValue.toLowerCase() : '';
};

export const getClaimIdFromNotification = (notification: any): string | null => {
  const direct =
    notification?.claimId ||
    notification?.reclamationId ||
    notification?.reclamation?.id ||
    notification?.data?.claimId ||
    notification?.data?.reclamationId ||
    notification?.meta?.claimId ||
    notification?.meta?.reclamationId;
  if (direct) return direct;

  const linkValue = notification?.link || parseNotificationBody(notification?.body)?.linkUrl;
  const linkText = typeof linkValue === 'string' ? linkValue : '';
  const claimMatch = linkText.match(/claims\/(\d+)/i) || linkText.match(/reclamations\/(\d+)/i);
  if (claimMatch) return claimMatch[1];

  const bodyText =
    parseNotificationBody(notification?.body)?.message ||
    (typeof notification?.body === 'string' ? notification.body : '');
  const textClaimMatch = bodyText.match(/claim\s*#\s*(\d+)/i);
  return textClaimMatch ? textClaimMatch[1] : null;
};

export const getNotificationSiteId = (notification: any, fallbackSiteId: number): number => {
  return (
    notification?.siteId ||
    notification?.data?.siteId ||
    notification?.meta?.siteId ||
    fallbackSiteId
  );
};

export const parseAndFormatNotification = (notification: any) => {
  const hasUnreadTracking = Array.isArray(notification?.trackingRecords)
    ? notification.trackingRecords.some((record: any) => record && record.read === false)
    : false;

  const parsedBody = parseNotificationBody(notification?.body);
  const rawMessage = typeof notification?.body === 'string' ? notification.body : '';
  const fallbackMessage = parsedBody.message || rawMessage;
  const sanitizedHtml = sanitizeHtmlContent(fallbackMessage);
  const plainTextMessage = stripHtmlTags(sanitizedHtml);

  const linkUrl = normalizeExternalLink(notification?.link || parsedBody.linkUrl);

  const mediaItems = Array.isArray(notification?.media) ? notification.media : [];
  const imageMedia = mediaItems.filter(
    (entry: any) => typeof entry?.mimeType === 'string' && entry.mimeType.startsWith('image')
  );
  const videoMedia = mediaItems.filter(
    (entry: any) => typeof entry?.mimeType === 'string' && entry.mimeType.startsWith('video')
  );

  const imageSrc = imageMedia[0]?.downloadUrl || parsedBody.imageData || parsedBody.imageUrl;
  const videoSrc = videoMedia[0]?.downloadUrl || parsedBody.videoData || parsedBody.videoUrl;

  const displayTitle =
    notification?.type === STALLED_CLAIM_NOTIFICATION_TYPE
      ? 'Claim requires approval/denial'
      : notification?.type === CLAIM_CREATED_NOTIFICATION_TYPE
      ? 'New claim created'
      : notification?.title;

  const isClaimEmailApproval =
    getNotificationType(notification) === CLAIM_EMAIL_APPROVAL_NOTIFICATION_TYPE;

  return {
    ...notification,
    hasUnreadTracking,
    message: plainTextMessage,
    messageHtml: sanitizedHtml,
    linkUrl,
    mediaItems,
    imageSrc,
    videoSrc,
    displayTitle,
    isClaimEmailApproval,
    truncatedTitle:
      displayTitle && displayTitle.length > 30 ? `${displayTitle.slice(0, 27)}...` : displayTitle,
    truncatedDescription:
      plainTextMessage && plainTextMessage.length > 80
        ? `${plainTextMessage.slice(0, 77)}...`
        : plainTextMessage,
  };
};

export const sortNotificationsByReadStatus = (notifications: any[]) => {
  return [...notifications].sort((a, b) => {
    const newest = a?.createdAt ? moment(a.createdAt).valueOf() : 0;
    const oldest = b?.createdAt ? moment(b.createdAt).valueOf() : 0;

    if (a.hasUnreadTracking && !b.hasUnreadTracking) return -1;
    if (!a.hasUnreadTracking && b.hasUnreadTracking) return 1;

    if (newest === oldest) return 0;
    return newest > oldest ? -1 : 1;
  });
};

export const getAutoLoginNotifications = (whatsNewNotifications: any[]) => {
  return (whatsNewNotifications || [])
    .filter((notification: any) => {
      const hasUnreadTracking = Array.isArray(notification?.trackingRecords)
        ? notification.trackingRecords.some((record: any) => record && record.read === false)
        : false;
      return notification?.showOnLogin && hasUnreadTracking;
    })
    .sort((a: any, b: any) => {
      const aDate = a?.createdAt ? moment(a.createdAt).valueOf() : 0;
      const bDate = b?.createdAt ? moment(b.createdAt).valueOf() : 0;
      return aDate - bDate;
    });
};
